import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router";
import { ReactFlowProvider } from "@xyflow/react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft } from "lucide-react";
import { useApi, usePermission } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Button, EmptyState, Skeleton, Toaster, confirmAnswer, notifySaveFailed } from "@ncp/ui";
import { repoKeys, repoPaths, useBranch, useRepo } from "./api";
import { clone, ensurePositions } from "./graph";
import { BuilderContext, createBuilderStore } from "./store";
import { BuilderWorkspace } from "./BuilderWorkspace";
import type { BranchDetail, RepoView } from "./types";

/** Full-screen Workflow Builder: /workflows/:key?branch=… */
export function WorkflowBuilderPage() {
  const { t } = useTranslation();
  const { dir } = useLocale();
  const { key = "" } = useParams();
  const [params] = useSearchParams();
  const branch = params.get("branch") ?? "main";
  const repo = useRepo(key);
  const detail = useBranch(key, branch);

  if (repo.isError || detail.isError) {
    return (
      <div className="grid h-screen place-items-center bg-sand-50">
        <EmptyState icon={AlertTriangle} title={t("admin:builder.notFound")} description={(repo.error ?? detail.error)?.message} action={<Button asChild variant="outline"><Link to="/workflows"><ArrowLeft /> {t("admin:builder.back")}</Link></Button>} />
      </div>
    );
  }
  if (!repo.data || !detail.data) {
    return <div className="flex h-screen flex-col bg-sand-50"><Skeleton className="h-14 rounded-none" /><div className="flex flex-1 gap-2 p-2"><Skeleton className="w-64" /><Skeleton className="flex-1" /><Skeleton className="w-[360px]" /></div></div>;
  }
  return (
    <>
      <BuilderSession key={`${key}:${branch}:${detail.data.head.commit.id}`} repo={repo.data} detail={detail.data} branch={branch} />
      <Toaster dir={dir} />
    </>
  );
}

function BuilderSession({ repo, detail, branch }: { repo: RepoView; detail: BranchDetail; branch: string }) {
  const { t } = useTranslation();
  const api = useApi();
  const qc = useQueryClient();
  const { can } = usePermission();
  const canWrite = can("platform.workflows.write") && detail.branch.status === "Open";
  const headGraph = useMemo(() => ensurePositions(clone(detail.head.graph)), [detail.head.graph]);
  const store = useMemo(() => {
    const s = createBuilderStore(headGraph, clone(detail.head.meta), !canWrite);
    if (detail.workingCopy && !detail.workingCopy.stale && canWrite) s.replace(ensurePositions(clone(detail.workingCopy.graph)), null, "Unsaved work");
    return s;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [staleCopy, setStaleCopy] = useState(detail.workingCopy?.stale && canWrite ? detail.workingCopy : null);
  useEffect(() => { store.setReadOnly(!canWrite); }, [canWrite, store]);

  return (
    <BuilderContext.Provider value={store}>
      <ReactFlowProvider>
        <div className="flex h-screen flex-col overflow-hidden bg-sand-50">
          {staleCopy ? (
            <div className="flex items-center gap-3 border-b border-warning-200 bg-warning-50 px-4 py-2 text-xs text-warning-900">
              <AlertTriangle className="size-4" /> {t("admin:builder.staleCopy")}
              <Button size="sm" variant="outline" onClick={() => { store.replace(ensurePositions(clone(staleCopy.graph)), null, "Restore unsaved work"); setStaleCopy(null); }}>{t("admin:builder.restoreCopy")}</Button>
              <Button size="sm" variant="ghost" onClick={() => {
                // The banner goes only once the API discarded the copy; a failure keeps it and says why.
                api.delete(repoPaths.workingCopy(repo.key, branch))
                  .then((answer) => { confirmAnswer(answer); setStaleCopy(null); void qc.invalidateQueries({ queryKey: repoKeys.branch(repo.key, branch) }); })
                  .catch((e: unknown) => notifySaveFailed(e));
              }}>{t("admin:builder.discardCopy")}</Button>
            </div>
          ) : null}
          <BuilderWorkspace repo={repo} detail={detail} branch={branch} canWrite={canWrite} headGraph={headGraph} />
        </div>
      </ReactFlowProvider>
    </BuilderContext.Provider>
  );
}
